import type { App } from '@keboola/api-client/developerPortal/types';
import { Package } from '@keboola/design/icons';

import { getIcon } from '../helpers';

type Props = {
  app: Pick<App, 'icon' | 'name'>;
  size?: 48 | 64;
};

/**
 * A component's icon, with a token-driven placeholder when it has none.
 *
 * The original shipped a bundled `default-app-icon.png`. A PNG cannot re-skin with the brand, so the
 * placeholder is a design-system glyph on `bg-muted` instead — see the deviations table in
 * `docs/parity-contract.md`.
 */
export function AppIcon({ app, size = 64 }: Props) {
  const src = getIcon(app);
  // Tailwind needs literal class names, so the two sizes are spelled out rather than interpolated.
  const box = size === 64 ? 'tw:size-16' : 'tw:size-12';

  if (src === null) {
    return (
      <div
        className={`${box} tw:flex tw:items-center tw:justify-center tw:rounded-md tw:bg-muted tw:text-muted-foreground`}
        // The placeholder carries no information the component's name doesn't, so it is decorative.
        aria-hidden="true"
      >
        <Package />
      </div>
    );
  }

  return (
    <img
      src={src}
      // The name is always rendered next to the icon, so a descriptive alt would be read twice by a
      // screen reader. Empty alt marks it decorative instead.
      alt=""
      width={size}
      height={size}
      loading="lazy"
      className={`${box} tw:rounded-md tw:object-contain`}
    />
  );
}
